import { useMemo } from 'react';
import { Modal } from './Modal';
import { useCategories } from '../../hooks';
import { formatMXN } from '../../utils/format';
import type { Transaction } from '../../types';

interface BinTransactionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  binLabel: string;
  transactions: Transaction[];
}

export function BinTransactionsModal({
  isOpen,
  onClose,
  binLabel,
  transactions,
}: BinTransactionsModalProps) {
  const categories = useCategories();

  const categoryMap = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((cat) => map.set(cat.id, cat.name));
    return map;
  }, [categories]);

  const total = useMemo(
    () => transactions.reduce((sum, t) => sum + Math.abs(t.amount), 0),
    [transactions]
  );

  const title = `Transacciones: ${binLabel}`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="space-y-4">
        {transactions.length === 0 ? (
          <p className="text-gray-500 text-center py-4">
            No hay transacciones en este rango
          </p>
        ) : (
          <>
            <ul className="divide-y divide-gray-200 max-h-80 overflow-y-auto">
              {transactions.map((transaction) => (
                <li
                  key={transaction.id}
                  className="py-3 flex items-center justify-between"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {transaction.description}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{transaction.date}</span>
                      {transaction.categoryId && (
                        <>
                          <span>•</span>
                          <span>
                            {categoryMap.get(transaction.categoryId) ??
                              'Sin categoría'}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <span className="ml-4 text-sm font-semibold text-red-600">
                    {formatMXN(Math.abs(transaction.amount))}
                  </span>
                </li>
              ))}
            </ul>

            <div className="border-t border-gray-200 pt-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">
                  Total ({transactions.length} transacciones)
                </span>
                <span className="text-lg font-bold text-red-600">
                  {formatMXN(total)}
                </span>
              </div>
            </div>
          </>
        )}

        <div className="flex justify-end mt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </Modal>
  );
}
